<template>
  <div class="email-delivery-page">
    <header class="page-header">
      <router-link class="back-link" :to="backTarget">
        {{ backLabel }}
      </router-link>
      <h1>Email delivery</h1>
      <p class="page-purpose">
        Gap emails sent for
        <strong>{{ report?.project?.name || 'this project' }}</strong>.
        This shows whether receiving servers took the mail, whether any of it
        bounced or was marked as spam, and how many stores sent a certificate back.
      </p>
    </header>

    <div v-if="!projectId" class="empty-panel">
      <p>Open this from a project.</p>
    </div>

    <template v-else>
      <div class="window-switch" role="group" aria-label="Time window">
        <button
          v-for="option in windows"
          :key="option.key"
          type="button"
          class="window-btn"
          :class="{ active: windowKey === option.key }"
          :aria-pressed="windowKey === option.key"
          @click="windowKey = option.key"
        >
          {{ option.label }}
        </button>
      </div>

      <p v-if="loading && !report" class="status-line">Checking delivery receipts…</p>
      <p v-else-if="error" class="status-line status-error">{{ error }}</p>

      <template v-else-if="report">
        <section class="verdict" :class="`band-${report.verdict.band}`">
          <h2>{{ report.verdict.headline }}</h2>
          <p>{{ report.verdict.detail }}</p>
          <p v-if="report.window" class="window-note">
            {{ report.window.label }}
            <span v-if="report.window.key !== 'all' && report.window.since">
              · since {{ formatWhen(report.window.since) }}
            </span>
          </p>
        </section>

        <section v-if="report.counts.sent" class="meter-row">
          <article
            v-for="meter in report.meters"
            :key="meter.key"
            class="meter"
            :class="`band-${meter.band}`"
          >
            <p class="meter-label">{{ meter.label }}</p>
            <p class="meter-rate">{{ meter.rate_label || '—' }}</p>
            <p class="meter-band">{{ meter.band_label }}</p>
            <p class="meter-count">
              {{ meter.count }} of {{ meter.of }} {{ meter.of_label }}
            </p>
            <p class="meter-standard">{{ meter.standard }}</p>
          </article>
        </section>

        <section v-if="report.counts.sent" class="count-panel">
          <h2>What happened to the emails</h2>
          <ul class="count-list">
            <li><strong>{{ report.counts.sent }}</strong> sent</li>
            <li><strong>{{ report.counts.accepted }}</strong> accepted by the receiving server</li>
            <li><strong>{{ report.counts.waiting }}</strong> still waiting on a receipt</li>
            <li><strong>{{ report.counts.bounced }}</strong> bounced</li>
            <li><strong>{{ report.counts.complaint }}</strong> marked as spam</li>
            <li><strong>{{ report.counts.failed }}</strong> could not be sent</li>
          </ul>
          <p v-if="report.counts.untracked" class="aside">
            {{ report.counts.untracked }} went out before delivery tracking, so they are left out of the rates.
          </p>
          <p class="aside">{{ report.placement_note }}</p>
        </section>

        <section v-if="report.counts.sent" class="count-panel">
          <h2>What people did</h2>
          <p class="outcome-lead">
            <strong>{{ report.outcome.certificates_received }}</strong>
            of
            <strong>{{ report.outcome.stores_emailed }}</strong>
            stores have had a new certificate checked since the email.
          </p>
          <ul class="count-list">
            <li><strong>{{ report.outcome.opened }}</strong> emails opened</li>
            <li><strong>{{ report.outcome.clicked }}</strong> emails with a link clicked</li>
          </ul>
          <p class="aside">{{ report.outcome.note }}</p>
        </section>

        <section v-if="report.counts.sent" class="count-panel">
          <h2>Addresses that did not go through</h2>
          <p v-if="!report.problems.length" class="aside">
            No bounced, spam, or unsent addresses in this window.
          </p>
          <template v-else>
            <p v-if="report.problem_total > report.problems.length" class="aside">
              Showing {{ report.problems.length }} of {{ report.problem_total }}.
            </p>
            <table class="problem-table">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>What happened</th>
                  <th>Stores</th>
                  <th>Sent</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in report.problems" :key="`${row.kind}-${row.to_email}-${row.sent_at}`">
                  <td>
                    <div>{{ row.to_name || row.to_email }}</div>
                    <div v-if="row.to_name" class="aside">{{ row.to_email }}</div>
                  </td>
                  <td>
                    <div>{{ row.kind_label }}</div>
                    <div v-if="row.reason" class="aside">{{ row.reason }}</div>
                  </td>
                  <td>{{ storeLabel(row) }}</td>
                  <td>{{ formatWhen(row.sent_at) }}</td>
                </tr>
              </tbody>
            </table>
          </template>
        </section>
      </template>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/services/api'

const route = useRoute()
const loading = ref(false)
const error = ref('')
const report = ref(null)
const windowKey = ref('today')

const windows = [
  { key: 'today', label: 'Today' },
  { key: '24h', label: 'Last 24 hours' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'all', label: 'All time' },
]

const projectId = computed(() => {
  const fromParam = route.params.projectId
  if (fromParam) return String(fromParam)
  const raw = route.query.project_id
  const value = Array.isArray(raw) ? raw[0] : raw
  return value ? String(value) : ''
})

const onProjectPath = computed(() => Boolean(route.params.projectId))

const backTarget = computed(() => (
  onProjectPath.value
    ? `/projects/${projectId.value}`
    : { name: 'ComplianceHealthDashboard', query: projectId.value ? { project_id: projectId.value } : {} }
))

const backLabel = computed(() => (
  onProjectPath.value ? 'Back to project' : 'Back to Compliance Health'
))

const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

function formatWhen(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function storeLabel(row) {
  const names = (row.stores || []).map((store) => store.name).filter(Boolean)
  const extra = (row.store_count || 0) - names.length
  if (!names.length) {
    return row.store_count ? `${row.store_count} stores` : ''
  }
  if (extra > 0) return `${names.join(', ')}, and ${extra} more`
  return names.join(', ')
}

async function load() {
  if (!projectId.value) {
    report.value = null
    error.value = ''
    loading.value = false
    return
  }
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.get('/compliance-health/email-delivery/', {
      params: {
        project_id: projectId.value,
        window: windowKey.value,
        tz: viewerTz,
      },
    })
    report.value = data
  } catch (err) {
    report.value = null
    const status = err?.response?.status
    error.value = status === 403
      ? 'This project is outside your organization.'
      : 'Could not load email delivery.'
  } finally {
    loading.value = false
  }
}

watch([projectId, windowKey], load, { immediate: true })
</script>

<style scoped>
.email-delivery-page {
  max-width: 960px;
  margin: 0 auto;
  padding: 1.5rem 1.25rem 3rem;
  color: #1e293b;
}

.back-link {
  display: inline-block;
  margin-bottom: 0.75rem;
  color: #2563eb;
  text-decoration: none;
  font-size: 0.875rem;
}

.page-header h1 {
  margin: 0 0 0.35rem;
  font-size: 1.6rem;
}

.page-purpose {
  margin: 0;
  max-width: 42rem;
  color: #334155;
  line-height: 1.45;
}

.window-switch {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 1.25rem 0;
}

.window-btn {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
  border-radius: 999px;
  padding: 0.35rem 0.8rem;
  cursor: pointer;
}

.window-btn.active {
  background: #1e293b;
  border-color: #1e293b;
  color: #fff;
}

.status-line {
  color: #64748b;
}

.status-error {
  color: #991b1b;
}

.verdict,
.meter,
.count-panel,
.empty-panel {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
}

.verdict,
.count-panel,
.empty-panel {
  padding: 1rem 1.1rem;
  margin-bottom: 1rem;
}

.verdict h2,
.count-panel h2 {
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
}

.verdict p,
.outcome-lead {
  margin: 0.25rem 0 0;
  line-height: 1.45;
}

.window-note,
.aside,
.meter-standard,
.meter-count {
  color: #64748b;
  font-size: 0.85rem;
  line-height: 1.4;
}

.meter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.meter {
  flex: 1 1 220px;
  max-width: 320px;
  padding: 0.9rem 1rem;
}

.meter-label {
  margin: 0;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #64748b;
}

.meter-rate {
  margin: 0.15rem 0 0;
  font-size: 1.8rem;
  font-weight: 600;
  line-height: 1.1;
}

.meter-band {
  margin: 0.15rem 0 0.35rem;
  font-weight: 600;
}

.band-healthy {
  background: #f0fdf4;
}

.band-watch {
  background: #fffbeb;
}

.band-problem {
  background: #fef2f2;
}

.band-unknown {
  background: #f8fafc;
}

.band-healthy .meter-band,
.band-healthy.verdict h2 {
  color: #166534;
}

.band-watch .meter-band,
.band-watch.verdict h2 {
  color: #92400e;
}

.band-problem .meter-band,
.band-problem.verdict h2 {
  color: #991b1b;
}

.count-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.25rem;
  margin: 0.5rem 0 0;
  padding: 0;
  list-style: none;
}

.problem-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.75rem;
  font-size: 0.9rem;
}

.problem-table th,
.problem-table td {
  text-align: left;
  vertical-align: top;
  padding: 0.55rem 0.4rem;
  border-top: 1px solid #e2e8f0;
}

.problem-table th {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #64748b;
  border-top: 0;
}
</style>
